import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Gamepad2,
  GraduationCap,
  Layers,
  Monitor,
  Radar,
  Tent,
  Trophy,
  Wrench,
} from 'lucide-react';
import {
  CONTENT_MAX_WIDE,
  CONTENT_INSET,
  pageTopPadding,
} from '../styles/layout';

export const CATALOG_PATH = '/Catalog';

const GOLD = '#C9A84C';
const BG = '#090909';

const EXPERIENCE_MEDIA = [
  {
    id: 'experience-featured',
    src: '/Event1Catalog.mov',
    kind: 'clip',
    featured: true,
  },
  { id: 'experience-event-3', src: '/Event3Catalog.MP4', kind: 'clip' },
  { id: 'experience-event-2', src: '/Event2Catalog.mov', kind: 'clip' },
];

const OFFERINGS = [
  {
    icon: Tent,
    title: 'Inflatable golf enclosure',
    description: 'A fully enclosed, weather-ready bay that turns any venue into a course.',
    media: { id: 'offer-inflatable', src: '/InflatableCatalog.JPG', kind: 'photo' },
  },
  {
    icon: Monitor,
    title: '4K ultra-HD projector',
    description: 'Crystal-clear course visuals on a full-size impact screen.',
    media: { id: 'offer-projector', src: '/ProjectorCatalog.webp', kind: 'photo', fit: 'contain' },
  },
  {
    icon: Radar,
    title: 'Professional launch monitor',
    description: 'Accurate ball flight, distance, and shot data for every swing.',
    media: { id: 'offer-monitor', src: '/LaunchMonitorCatalog.jpeg', kind: 'photo', fit: 'contain' },
  },
  {
    icon: Layers,
    title: 'Premium hitting mat & turf',
    description: 'Tour-quality surface so every shot feels like the real thing.',
    media: { id: 'offer-turf', src: '/TurfCatalog.jpg', kind: 'photo' },
    overlay: true,
  },
  {
    icon: GraduationCap,
    title: 'Coaching',
    description: 'On-site instruction and shot data so every guest can actually improve.',
    media: { id: 'offer-coaching', src: '/CoachingCatalog.avif', kind: 'photo' },
  },
  {
    icon: Trophy,
    title: 'Tournaments',
    description: 'Structured competitions that keep groups moving, scoring, and engaged.',
    media: { id: 'offer-tournament', src: '/TournamentCatalog.jpg', kind: 'photo' },
  },
  {
    icon: Gamepad2,
    title: 'Screens, games & competitions',
    description: '200+ world-class courses plus minigames, closest-to-the-pin, and more.',
    media: { id: 'offer-games', src: '/MinigamesCatalog.jpg', kind: 'photo' },
  },
  {
    icon: Wrench,
    title: 'Full setup, staff & clubs',
    description: 'We deliver, install, run the night, and tear down. Clubs included.',
    media: { id: 'offer-staff', src: '/ClubCatalog.webp', kind: 'photo' },
  },
];

const isVideoSrc = (src, kind) => {
  if (src) return /\.(mp4|webm|mov)(\?|$)/i.test(src);
  return kind === 'clip';
};

function MediaSlot({ src, kind = 'photo', caption, alt, featured, compact, fit, shape }) {
  const hasMedia = Boolean(src);
  const isVideo = isVideoSrc(src, kind);
  const label = isVideo ? 'Video' : 'Photo';
  const frameClass = [
    'dd-catalog-media-frame',
    fit === 'contain' ? 'dd-catalog-media-frame--contain' : '',
    shape === 'square' ? 'dd-catalog-media-frame--square' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <figure className={`dd-catalog-media-figure${featured ? ' dd-catalog-media-figure--featured' : ''}`}>
      {hasMedia && isVideo ? (
        <video
          className={frameClass}
          src={src}
          autoPlay
          muted
          loop
          playsInline
          aria-label={alt || caption || label}
        />
      ) : hasMedia ? (
        <img
          className={frameClass}
          src={src}
          alt={alt || caption || label}
        />
      ) : (
        <div
          className="dd-catalog-media-frame dd-catalog-media-placeholder"
          role="img"
          aria-label={`${label} coming soon`}
        >
          <span className="dd-catalog-media-badge">{label}</span>
        </div>
      )}
      {caption && !compact ? (
        <figcaption className="dd-catalog-media-caption">{caption}</figcaption>
      ) : null}
    </figure>
  );
}

const Catalog = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (location.pathname !== CATALOG_PATH) {
    return <Navigate to={`${CATALOG_PATH}${location.search}${location.hash}`} replace />;
  }

  const [featured, ...thumbs] = EXPERIENCE_MEDIA;

  const container = {
    maxWidth: CONTENT_MAX_WIDE,
    margin: '0 auto',
    padding: `0 ${CONTENT_INSET}`,
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div
      className="dd-page dd-catalog"
      style={{
        backgroundColor: BG,
        color: '#F5F0E8',
        paddingTop: pageTopPadding(isMobile),
      }}
    >
      <Helmet>
        <title>Catalog | Digital Divot Golf</title>
        <meta
          name="description"
          content="See Digital Divot's inflatable golf simulator, event formats, and everything we bring to mobile golf experiences in Arizona."
        />
        <meta property="og:title" content="Catalog | Digital Divot Golf" />
        <meta
          property="og:description"
          content="A full golf experience delivered to your venue — inflatable bay, competition, and complete setup."
        />
        <meta property="og:url" content={`https://digitaldivotgolf.com${CATALOG_PATH}`} />
        <link rel="canonical" href={`https://digitaldivotgolf.com${CATALOG_PATH}`} />
      </Helmet>

      <section className="dd-catalog-hero">
        <div style={{ ...container, textAlign: 'center' }}>
          <span className="dd-catalog-eyebrow">Catalog</span>
          <h1 className="dd-catalog-hero-title">See Digital Divot in action</h1>
          <p className="dd-catalog-hero-lead">
            Our inflatable bay, the events we host, and everything that shows up with us.
          </p>
        </div>
      </section>

      <section id="experience" className="dd-catalog-section">
        <div style={container}>
          <div className="dd-catalog-gallery">
            <div className="dd-catalog-gallery-featured">
              <MediaSlot {...featured} featured />
            </div>
            <div className="dd-catalog-gallery-thumbs">
              {thumbs.map((item) => (
                <MediaSlot key={item.id} {...item} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="setup" className="dd-catalog-section">
        <div style={container}>
          <div className="dd-catalog-intro dd-catalog-intro--center">
            <span className="dd-catalog-eyebrow">What We Offer</span>
            <h2 className="dd-catalog-title">The full Digital Divot setup</h2>
            <p className="dd-catalog-lead dd-catalog-lead--center">
              Every booking arrives fully equipped. No hidden extras — just the enclosure, the
              tech, the turf, and a crew that runs the night.
            </p>
          </div>

          <div className="dd-catalog-offerings-grid">
            {OFFERINGS.map((item) => {
              const Icon = item.icon;
              return (
                <article
                  key={item.title}
                  className={`dd-catalog-offering-card${item.overlay ? ' dd-catalog-offering-card--overlay' : ''}`}
                >
                  <MediaSlot {...item.media} compact />
                  <div className="dd-catalog-offering-copy">
                    <Icon size={18} color={GOLD} strokeWidth={2} aria-hidden="true" />
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="dd-catalog-cta">
        <div style={container}>
          <h2 className="dd-catalog-cta-title">Ready to book the experience?</h2>
          <div className="dd-catalog-hero-ctas">
            <Link to="/booking" className="dd-catalog-btn dd-catalog-btn--primary">
              Book Now
              <ArrowRight size={16} />
            </Link>
            <Link to="/packages" className="dd-catalog-btn dd-catalog-btn--ghost">
              View Packages
            </Link>
          </div>
          <a href="mailto:events@digitaldivotgolf.com" className="dd-catalog-text-link">
            Have a unique event? Talk to us
            <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Catalog;
